import { useCallback, useEffect, useState } from "react";
import { isFulfilled } from "@reduxjs/toolkit";
import { GridPreProcessEditCellProps } from "@mui/x-data-grid-pro";
import { useTranslation } from "react-i18next";

import { useAppDispatch } from "../../app/hooks";
import {
  fetchAccounts,
  fetchIncomeTypes,
  updateAccounts,
  updateIncomeTypes,
} from "./settingsActions";
import { DataGrid } from "./helpers/DataGrid";
import { Account, Accounts, IncomeType, IncomeTypes } from "./settingsApi";
import { handleAccountsData, handleIncomeTypesData } from "./helpers/utils";
import { Box } from "@mui/material";
import { Heading3 } from "../../common/components/styled/styled";

export const AccountsControlPanel = () => {
  const { t } = useTranslation("settings");
  const dispatch = useAppDispatch();
  const [accountsData, setAccountsData] = useState<Accounts>({
    accounts: [],
    version: 0,
  });
  const [accountRows, setAccountRows] = useState<Account[]>([]);
  const [incomeTypesData, setIncomeTypesData] = useState<IncomeTypes>({
    incomeTypes: [],
    version: 0,
  });
  const [incomeTypeRows, setIncomeTypeRows] = useState<IncomeType[]>([]);

  const validateCode = (code: string) => {
    const lengthNotValid = code.length < 2 || code.length > 8;
    const accountCodePattern = /^[\w-_]+$/;
    const patternNotValid = !code.match(accountCodePattern);
    if (lengthNotValid) {
      return t("validationAccountCodeLength");
    } else if (patternNotValid) {
      return t("codeCannotContainSpecialCharacters");
    }
    return null;
  };

  const validateName = (name: string) => {
    const isNotValid = name.length < 1 || name.length > 100;
    return isNotValid ? t("validationAccountNameLength") : null;
  };

  const preProcessCodeCellProps = (params: GridPreProcessEditCellProps) => {
    const errorMessage = validateCode(params.props.value!.toString());
    return { ...params.props, error: errorMessage };
  };

  const preProcessNameCellProps = (params: GridPreProcessEditCellProps) => {
    const errorMessage = validateName(params.props.value!.toString());
    return { ...params.props, error: errorMessage };
  };

  const accountColumns = [
    {
      field: "accountCode",
      headerName: t("accountCode"),
      width: 180,
      editable: true,
      preProcessEditCellProps: preProcessCodeCellProps,
    },
    {
      field: "name",
      headerName: t("nameFi"),
      width: 280,
      editable: true,
      preProcessEditCellProps: preProcessNameCellProps,
    },
  ];

  const incomeTypeColumns = [
    {
      field: "incomeCode",
      headerName: t("incomeCode"),
      width: 180,
      editable: true,
      preProcessEditCellProps: preProcessCodeCellProps,
    },
    {
      field: "name",
      headerName: t("nameFi"),
      width: 280,
      editable: true,
      preProcessEditCellProps: preProcessNameCellProps,
    },
  ];

  const accountsNewRow = {
    accountCode: "",
    name: "",
  };

  const incomeTypesNewRow = {
    incomeCode: "",
    name: "",
  };

  const saveIncomeTypes = (modifiedIncomeTypes: IncomeType[]) => {
    const array = modifiedIncomeTypes
      .filter((item: any) => item.status !== "deleted")
      .reduce((map: any, obj: any) => {
        map[obj.incomeCode] = obj.name;
        return map;
      }, {});
    const newIncomeTypes: IncomeTypes = {
      version: incomeTypesData.version,
      incomeTypes: array,
    };
    dispatch(updateIncomeTypes(newIncomeTypes)).then(
      (action) => isFulfilled(action) && setIncomeTypesData(action.payload),
    );
  };

  const saveAccounts = (modifiedAccounts: Account[]) => {
    const array = modifiedAccounts
      .filter((item: any) => item.status !== "deleted")
      .reduce((map: any, obj: any) => {
        map[obj.accountCode] = obj.name;
        return map;
      }, {});
    const newAccounts: Accounts = {
      version: accountsData.version,
      accounts: array,
    };
    dispatch(updateAccounts(newAccounts)).then(
      (action) => isFulfilled(action) && setAccountsData(action.payload),
    );
  };

  const getIncomeTypes = useCallback(() => {
    dispatch(fetchIncomeTypes({})).then(
      (action) => isFulfilled(action) && setIncomeTypesData(action.payload),
    );
  }, [dispatch]);

  const getAccounts = useCallback(() => {
    dispatch(fetchAccounts({})).then(
      (action) => isFulfilled(action) && setAccountsData(action.payload),
    );
  }, [dispatch]);

  useEffect(() => {
    getIncomeTypes();
    getAccounts();
  }, [getIncomeTypes, getAccounts]);

  useEffect(() => {
    if (accountsData !== undefined) {
      const array = handleAccountsData(accountsData.accounts);
      setAccountRows(array);
    }
  }, [accountsData]);

  useEffect(() => {
    if (incomeTypesData !== undefined) {
      const array = handleIncomeTypesData(incomeTypesData.incomeTypes);
      setIncomeTypeRows(array);
    }
  }, [incomeTypesData]);

  return (
    <>
      <Box>
        <Heading3>{t("accounts")}</Heading3>
        <DataGrid
          columns={accountColumns}
          rows={accountRows}
          newRow={accountsNewRow}
          onSave={saveAccounts}
          onUndo={getAccounts}
          hideSave={false}
          actions={["delete"]}
        />
      </Box>
      <Box mt={4}>
        <Heading3>{t("incomeTypes")}</Heading3>
        <DataGrid
          columns={incomeTypeColumns}
          rows={incomeTypeRows}
          newRow={incomeTypesNewRow}
          onSave={saveIncomeTypes}
          onUndo={getIncomeTypes}
          hideSave={false}
          actions={["delete"]}
        />
      </Box>
    </>
  );
};
